import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertTriangle, Wrench, KeyRound, Eye, EyeOff, Lock, Loader2, FileText, Receipt, ScrollText, ShieldCheck, MessagesSquare, LifeBuoy, Car, CalendarDays, MessageCircle, Handshake, BarChart3, Sparkles, UserRound } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useQuery } from '@apollo/client';
import { sendOTP, verifyOTP, loginWithPassword, loginDipendente, datiSessioneDipendente, exchangeBridgeCode, exchangeRepresentativeBridgeCode, saveToken, saveAssociateData, getToken, logout, LocationOption, DipendenteSessione } from '../services/otpAuthService';
import { ricollegaPushDipendente } from '../hooks/usePushNotifications';
import { getPublicSettings } from '../services/certifiedNetworkService';
import { resolveBridgeNext, resolveRepresentativeBridgeNext } from '../utils/bridgeNext';
import { safeNext } from '../utils/safeNext';
import { saveRepresentativeToken } from '../services/representativeService';
import { stessaPersonaDelToken } from '../utils/sezioniDipendente';
import { GET_MAINTENANCE_STATUS, MaintenanceStatus } from '../services/queries';
import LocationSelectionModal from '../components/LocationSelectionModal';
import '../styles/associate-login.css';
import logoWhite from '../assets/logo-unasca-white.png';

/**
 * Pagina di login per associati con Partita IVA e OTP
 */
const AssociateLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Redirect post-login: da ?redirect= (es. dirette riservate) oppure da location.state.from
  // (impostato dalle guardie AssociatePrivateRoute/RepresentativePrivateRoute quando un link
  // protetto, es. /area-riservata/eventi, viene aperto senza essere loggati).
  // Accetta solo path interni (anti open-redirect).
  const isInternalPath = (p: unknown): p is string =>
    typeof p === 'string' && p.startsWith('/') && !p.startsWith('//');
  // ?next= (link ticket scaduto, Task 27): solo percorsi interni; col ponte app `next` resta del ponte.
  const rawRedirect = searchParams.get('redirect') || (searchParams.get('bridge') ? null : safeNext(searchParams.get('next')));
  const stateFrom = (location.state as { from?: string } | null)?.from;
  const redirectTo = isInternalPath(rawRedirect) ? rawRedirect : (isInternalPath(stateFrom) ? stateFrom : null);
  const [step, setStep] = useState<1 | 2>(1); // 1 = P.IVA, 2 = OTP
  const [partitaIVA, setPartitaIVA] = useState('');
  const [otpCode, setOTPCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [expiresIn, setExpiresIn] = useState(10);
  const [loading, setLoading] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [selectedAssociateId, setSelectedAssociateId] = useState<string | null>(null);

  // Multi-sede state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locations, setLocations] = useState<LocationOption[]>([]);

  // Representative login state
  const [loginTipo, setLoginTipo] = useState<'associate' | 'representative'>('associate');
  const [representativeId, setRepresentativeId] = useState<string | null>(null);

  // Modalita' di accesso: codice via email (default, invariato), password oppure dipendente dello studio
  const [mode, setMode] = useState<'otp' | 'password' | 'dipendente'>('otp');
  const [password, setPassword] = useState('');
  // Dipendente: nome e cognome come li ha registrati il titolare (P.IVA e password in comune con le altre schede)
  const [nominativoDipendente, setNominativoDipendente] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Scheda «Sono un dipendente» solo se la funzione non è spenta (`dipendentiLoginVisibile` delle
  // impostazioni pubbliche); campo assente o impostazioni non raggiungibili ⇒ le due schede di sempre
  const [schedaDipendente, setSchedaDipendente] = useState(false);

  useEffect(() => {
    let annullato = false;
    Promise.resolve()
      .then(() => getPublicSettings())
      .then((risposta) => {
        const dati = risposta?.data as { dipendentiLoginVisibile?: unknown } | undefined;
        if (!annullato) setSchedaDipendente(dati?.dipendentiLoginVisibile === true);
      })
      .catch(() => {
        // scheda nascosta
      });
    return () => { annullato = true; };
  }, []);

  // Login sopra una sessione già aperta (PC condiviso): prima il logout completo, che sospende la
  // push web legata al token precedente, poi il nuovo token. Solo a login riuscito.
  // Se rientra la stessa persona (stesso titolare, o stesso dipendente) niente logout: sospenderebbe
  // la push web di questo browser, che si riattiva solo ricaricando la pagina.
  const chiudiSessionePrecedente = (nuovoToken: string) => {
    const precedente = getToken();
    if (precedente && !stessaPersonaDelToken(precedente, nuovoToken)) logout();
  };

  // Legge P.IVA precompilata dalla query string (es. ?piva=01234567890)
  useEffect(() => {
    const pivaParam = searchParams.get('piva');
    if (pivaParam) {
      setPartitaIVA(pivaParam);
    }
  }, [searchParams]);

  // Ponte dall'app mobile: ?bridge=<codice monouso> [&next=/percorso]
  // Inerte se il parametro non c'e': il flusso OTP normale non viene toccato.
  // Con tipo=rappresentante il codice e' del ponte del rappresentante: si salva SOLO
  // representative_token (la sessione dello studio, se c'e', resta com'e').
  useEffect(() => {
    const bridge = searchParams.get('bridge');
    if (!bridge) return;
    let cancelled = false;
    if (searchParams.get('tipo') === 'rappresentante') {
      const fallito = (message?: string) => {
        toast.error(message || 'Collegamento scaduto. Accedi con la tua email personale.');
        navigate('/accesso-associati', { replace: true });
      };
      (async () => {
        setLoading(true);
        try {
          const response = await exchangeRepresentativeBridgeCode(bridge);
          if (cancelled) return;
          setLoading(false);
          if (response.success && response.data?.token) {
            saveRepresentativeToken(response.data.token);
            navigate(resolveRepresentativeBridgeNext(searchParams.get('next')), { replace: true });
          } else {
            fallito(response.message);
          }
        } catch (error: any) {
          if (cancelled) return;
          setLoading(false);
          fallito(error?.message);
        }
      })();
      return () => { cancelled = true; };
    }
    (async () => {
      setLoading(true);
      try {
        const response = await exchangeBridgeCode(bridge);
        if (cancelled) return;
        setLoading(false);
        if (response.success && response.data) {
          chiudiSessionePrecedente(response.data.token);
          saveToken(response.data.token);
          // Ponte di un dipendente: se il server manda anche `dipendente`, si salva come al suo login
          const datiPonte = response.data as typeof response.data & { dipendente?: DipendenteSessione };
          saveAssociateData(datiPonte.dipendente
            ? datiSessioneDipendente({ token: datiPonte.token, associate: datiPonte.associate, dipendente: datiPonte.dipendente, ruolo: 'dipendente' })
            : response.data.associate);
          if (datiPonte.dipendente) ricollegaPushDipendente(datiPonte.token);
          navigate(resolveBridgeNext(searchParams.get('next')), { replace: true });
        } else {
          toast.error(response.message || 'Collegamento scaduto. Accedi con il codice OTP.');
          navigate('/accesso-associati', { replace: true });
        }
      } catch (error: any) {
        if (cancelled) return;
        setLoading(false);
        toast.error(error?.message || 'Collegamento scaduto. Accedi con il codice OTP.');
        navigate('/accesso-associati', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Controlla stato manutenzione
  const { data: maintenanceData, loading: maintenanceLoading } = useQuery<{ maintenanceStatus: MaintenanceStatus }>(
    GET_MAINTENANCE_STATUS,
    {
      fetchPolicy: 'network-only' // Sempre aggiornato
    }
  );

  /**
   * Invia OTP (con supporto multi-sede)
   */
  const sendOTPRequest = async (associateId?: string) => {
    setLoading(true);

    try {
      const response = await sendOTP(partitaIVA.trim(), associateId);

      // Se ci sono più sedi, mostra il modale di selezione
      if (response.multipleLocations && response.locations) {
        setLocations(response.locations);
        setShowLocationModal(true);
        setLoading(false);
        return;
      }

      if (response.success && response.data) {
        setMaskedEmail(response.data.email);
        setExpiresIn(response.data.expiresIn);
        if (response.data.associateId) {
          setSelectedAssociateId(response.data.associateId);
        }
        // Handle representative OTP response
        if ((response.data as any).tipo === 'representative') {
          setLoginTipo('representative');
          setRepresentativeId((response.data as any).representativeId || null);
        } else {
          setLoginTipo('associate');
          setRepresentativeId(null);
        }
        setStep(2);
        toast.success('Codice OTP inviato via email!');
      } else {
        toast.error(response.message || 'Errore durante l\'invio del codice');
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante l\'invio del codice OTP');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Step 1: Invia OTP
   */
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!partitaIVA.trim()) {
      toast.error('Inserisci la Partita IVA o l\'email');
      return;
    }

    await sendOTPRequest();
  };

  /**
   * Gestisce la selezione di una sede dal modale
   */
  const handleLocationSelect = async (locationId: string) => {
    setShowLocationModal(false);
    setSelectedAssociateId(locationId);
    await sendOTPRequest(locationId);
  };

  /**
   * Step 2: Verifica OTP
   */
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode.trim()) {
      toast.error('Inserisci il codice OTP');
      return;
    }

    if (otpCode.trim().length !== 6) {
      toast.error('Il codice OTP deve essere di 6 cifre');
      return;
    }

    setLoading(true);

    try {
      // Representative OTP verification
      if (loginTipo === 'representative' && representativeId) {
        const response = await verifyOTP('', otpCode.trim(), undefined, representativeId);

        if (response.success && response.data) {
          saveRepresentativeToken(response.data.token);
          toast.success('Accesso area associativa effettuato!');
          // Un rappresentante non puo essere reindirizzato verso /area-riservata (token diverso → loop).
          // Ogni altra destinazione interna (es. /diretta/*) resta valida.
          navigate(redirectTo && !redirectTo.startsWith('/area-riservata') ? redirectTo : '/area-associativa');
        } else {
          if (response.attemptsRemaining !== undefined) {
            setAttemptsRemaining(response.attemptsRemaining);
            toast.error(`${response.message} (Tentativi rimasti: ${response.attemptsRemaining})`);
          } else {
            toast.error(response.message || 'Codice OTP non valido');
          }
        }
        return;
      }

      // Associate OTP verification (existing flow)
      const response = await verifyOTP(partitaIVA.trim(), otpCode.trim(), selectedAssociateId || undefined);

      if (response.success && response.data) {
        chiudiSessionePrecedente(response.data.token);
        saveToken(response.data.token);
        saveAssociateData(response.data.associate);

        toast.success('Accesso effettuato con successo!');
        // Un associato non puo essere reindirizzato verso l'area associativa (token diverso → loop).
        navigate(redirectTo && !redirectTo.startsWith('/area-associativa') ? redirectTo : '/area-riservata');
      } else {
        if (response.attemptsRemaining !== undefined) {
          setAttemptsRemaining(response.attemptsRemaining);
          toast.error(`${response.message} (Tentativi rimasti: ${response.attemptsRemaining})`);
        } else {
          toast.error(response.message || 'Codice OTP non valido');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante la verifica del codice OTP');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Accesso con password (alternativa all'OTP). Stessa destinazione post-login del flusso OTP;
   * con password temporanea si atterra sul profilo con il form di cambio password aperto.
   */
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partitaIVA.trim()) { toast.error('Inserisci la Partita IVA o l\'email'); return; }
    if (!password) { toast.error('Inserisci la password'); return; }
    setLoading(true);
    try {
      const response = await loginWithPassword(partitaIVA, password);
      if (response.success && response.data) {
        chiudiSessionePrecedente(response.data.token);
        saveToken(response.data.token);
        saveAssociateData(response.data.associate);
        if (response.data.mustChangePassword) {
          toast.success('Accesso effettuato. Scegli ora la tua password definitiva.');
          navigate('/area-riservata?tab=profilo&password=cambia');
          return;
        }
        toast.success('Accesso effettuato con successo!');
        navigate(redirectTo && !redirectTo.startsWith('/area-associativa') ? redirectTo : '/area-riservata');
      } else {
        toast.error(response.message || 'Credenziali non valide');
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante l\'accesso');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Accesso del dipendente dello studio (P.IVA dello studio + nome e cognome + password).
   * Stesso token e stessi dati salvati del titolare (`associate_token`, letto anche dall'estensione
   * Takata), con `ruolo` e `dipendente` dentro `associate_data`. Errori 401/429/403 col messaggio del server.
   */
  const handleDipendenteLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!partitaIVA.trim()) { toast.error('Inserisci la Partita IVA dello studio'); return; }
    if (!nominativoDipendente.trim()) { toast.error('Inserisci nome e cognome'); return; }
    if (!password) { toast.error('Inserisci la password'); return; }
    setLoading(true);
    try {
      const response = await loginDipendente(partitaIVA, nominativoDipendente, password);
      if (response.success && response.data) {
        chiudiSessionePrecedente(response.data.token);
        saveToken(response.data.token);
        saveAssociateData(datiSessioneDipendente(response.data));
        ricollegaPushDipendente(response.data.token);
        toast.success('Accesso effettuato con successo!');
        navigate(redirectTo && !redirectTo.startsWith('/area-associativa') ? redirectTo : '/area-riservata');
      } else {
        toast.error(response.message || 'Credenziali non valide');
      }
    } catch (error: any) {
      toast.error(error.message || 'Errore durante l\'accesso');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Torna allo step 1
   */
  const handleBack = () => {
    setStep(1);
    setOTPCode('');
    setAttemptsRemaining(null);
    setSelectedAssociateId(null);
  };

  /**
   * Richiedi nuovo codice
   */
  const handleResendOTP = async () => {
    setOTPCode('');
    setAttemptsRemaining(null);
    // Usa lo stesso associateId se già selezionato
    await sendOTPRequest(selectedAssociateId || undefined);
  };

  // Mostra loading durante il controllo manutenzione
  if (maintenanceLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Se in manutenzione, mostra messaggio
  if (maintenanceData?.maintenanceStatus.isActive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12">
            {/* Icona Manutenzione */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-full mb-6 animate-pulse">
                <Wrench className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Sistema in Manutenzione
              </h1>
              <div className="bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 border-2 border-orange-300 dark:border-orange-700 rounded-xl p-6 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-800 dark:text-gray-200 text-left leading-relaxed">
                    {maintenanceData.maintenanceStatus.message}
                  </p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Stiamo lavorando per migliorare il servizio. L'accesso all'area riservata sarà ripristinato a breve.
              </p>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors shadow-lg"
              >
                <ArrowLeft className="w-5 h-5" />
                Torna alla Home
              </button>
            </div>

            {/* Info Contatti */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                Per assistenza urgente, contatta la Segreteria Nazionale UNASCA
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const inputClass = 'w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition';
  const primaryBtnClass = 'w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-base font-semibold shadow-lg shadow-primary-900/25 transition focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900 disabled:opacity-60 disabled:cursor-not-allowed';
  const linkClass = 'text-primary-700 dark:text-primary-400 font-medium hover:underline';

  return (
    <div className="relative grid min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-950 lg:grid-cols-2">
      {/* Pannello con foto e servizi dell'area riservata (solo desktop) */}
      <aside className="relative hidden overflow-hidden bg-primary-950 lg:flex">
        <img
          src="/images/accesso/studio-sera.webp"
          srcSet="/images/accesso/studio-sera-900.webp 900w, /images/accesso/studio-sera.webp 1500w"
          sizes="50vw"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30" />
        <div className="relative z-10 mt-auto w-full max-w-xl p-10 xl:p-14 text-white">
          <img src={logoWhite} alt="UNASCA" className="mb-8 h-12 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]" />
          <h2 className="mb-3 text-3xl xl:text-4xl font-bold tracking-tight">Il tuo studio, a portata di clic</h2>
          <p className="mb-8 text-base text-white/80">
            Nell'area riservata trovi i servizi dedicati agli associati UNASCA.
          </p>
          <ul className="grid grid-cols-2 gap-3 text-sm">
            {[
              { testo: 'Svincolo atti', Icona: FileText },
              { testo: 'Rimborso voucher', Icona: Receipt },
              { testo: 'Circolari', Icona: ScrollText },
              { testo: 'Rete Certificata', Icona: ShieldCheck },
              { testo: 'Verifica airbag Takata', Icona: Car },
              { testo: 'Eventi e dirette', Icona: CalendarDays },
              { testo: 'Chat associati', Icona: MessageCircle },
              { testo: 'Community', Icona: MessagesSquare },
              { testo: 'Collaborazioni tra studi', Icona: Handshake },
              { testo: 'Sondaggi', Icona: BarChart3 },
              { testo: 'Supporto dedicato', Icona: LifeBuoy },
            ].map(({ testo, Icona }) => (
              <li key={testo} className="flex items-center gap-2.5 rounded-xl bg-white/10 px-3 py-2.5 ring-1 ring-white/15 backdrop-blur-sm">
                <Icona className="h-4 w-4 flex-shrink-0 text-primary-300" aria-hidden="true" />
                {testo}
              </li>
            ))}
            <li className="flex items-center gap-2.5 rounded-xl border border-dashed border-primary-300/60 bg-primary-500/15 px-3 py-2.5 font-semibold text-primary-100">
              <Sparkles className="h-4 w-4 flex-shrink-0 text-primary-300" aria-hidden="true" />
              …e molti altri
            </li>
          </ul>
        </div>
      </aside>

      {/* Accesso */}
      <main className="flex items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-[460px]">
          <div className="mb-6 text-center lg:text-left">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-lg shadow-primary-900/20">
              <Lock className="h-5 w-5" aria-hidden="true" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Area riservata associati
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {step === 2
                ? (loginTipo === 'representative' ? 'Verifica del codice ricevuto' : <>Codice inviato a <strong className="text-gray-700 dark:text-gray-200">{maskedEmail}</strong></>)
                : 'Riservato agli associati UNASCA'}
            </p>
          </div>

          {/* Card */}
          <div className="rounded-3xl bg-white p-6 shadow-xl ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10 sm:p-8">
          {/* Selettore modalita' (solo al primo passo) */}
          {step === 1 && (
            <div className={schedaDipendente ? 'al-seg al-seg--tre mb-6' : 'al-seg mb-6'} data-mode={mode} role="tablist" aria-label="Modalità di accesso">
              <span className="al-seg__thumb" aria-hidden="true" />
              <button type="button" role="tab" aria-selected={mode === 'otp'} className="al-seg__btn" onClick={() => setMode('otp')}>
                <Mail className="w-4 h-4" /> Codice via email
              </button>
              <button type="button" role="tab" aria-selected={mode === 'password'} className="al-seg__btn" onClick={() => setMode('password')}>
                <KeyRound className="w-4 h-4" /> Password
              </button>
              {schedaDipendente && (
                <button type="button" role="tab" aria-selected={mode === 'dipendente'} className="al-seg__btn" onClick={() => setMode('dipendente')}>
                  <UserRound className="w-4 h-4" /> Sono un dipendente
                </button>
              )}
            </div>
          )}

          {step === 1 && mode === 'dipendente' ? (
            /* Passo 1, modalita' dipendente dello studio */
            <form onSubmit={handleDipendenteLogin} className="space-y-5">
              <div>
                <label htmlFor="dip-piva" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Partita IVA dello studio</label>
                <input
                  id="dip-piva"
                  type="text"
                  value={partitaIVA}
                  onChange={(e) => setPartitaIVA(e.target.value)}
                  placeholder="es. 01234567890"
                  autoComplete="organization"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="dip-nominativo" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Nome e cognome</label>
                <input
                  id="dip-nominativo"
                  type="text"
                  value={nominativoDipendente}
                  onChange={(e) => setNominativoDipendente(e.target.value)}
                  placeholder="es. Mario Rossi"
                  autoComplete="username"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="dip-password" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Password</label>
                <div className="relative">
                  <input
                    id="dip-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className={`${inputClass} pr-12`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className={primaryBtnClass}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                Accedi
              </button>

              <div className="al-sep pt-5 text-center text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                L'accesso te lo crea il titolare dello studio. Password dimenticata? Chiedi al titolare di impostarne una nuova.
              </div>
            </form>
          ) : step === 1 && mode === 'password' ? (
            /* Passo 1, modalita' password */
            <form onSubmit={handlePasswordLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Partita IVA o email</label>
                <input
                  type="text"
                  value={partitaIVA}
                  onChange={(e) => setPartitaIVA(e.target.value)}
                  placeholder="es. 01234567890"
                  autoComplete="username"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className={`${inputClass} pr-12`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className={primaryBtnClass}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                Accedi
              </button>

              <div className="al-sep pt-5 text-center text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                Password dimenticata?{' '}
                <button type="button" onClick={() => setMode('otp')} className={linkClass}>entra con il codice via email</button>{' '}
                e reimpostala dal tuo profilo.
              </div>
            </form>
          ) : step === 1 ? (
            /* Passo 1, modalita' codice via email */
            <form onSubmit={handleSendOTP} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Partita IVA o email</label>
                <input
                  type="text"
                  value={partitaIVA}
                  onChange={(e) => setPartitaIVA(e.target.value)}
                  placeholder="es. 01234567890"
                  autoComplete="username"
                  className={inputClass}
                  autoFocus
                />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Ti invieremo un codice di 6 cifre all'email registrata in UNASCA.
                </p>
              </div>

              <button type="submit" disabled={loading} className={primaryBtnClass}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Inviami il codice
              </button>

              <div className="al-sep pt-5 text-center text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                Inserisci la Partita IVA o l'email dello studio: ricevi un codice sull'email registrata in UNASCA ed entri senza password.
              </div>
            </form>
          ) : (
            /* Passo 2: codice OTP */
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Codice di 6 cifre</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={(e) => setOTPCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className={`${inputClass} text-center text-2xl font-mono tracking-[0.5em]`}
                  maxLength={6}
                  autoFocus
                />
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Scade tra <strong className="text-gray-700 dark:text-gray-200">{expiresIn} minuti</strong></span>
                  {attemptsRemaining !== null && attemptsRemaining > 0 && (
                    <span className="text-amber-600 dark:text-amber-400">Tentativi rimasti: {attemptsRemaining}</span>
                  )}
                </div>
              </div>

              <button type="submit" disabled={loading} className={primaryBtnClass}>
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                Verifica e accedi
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Indietro
              </button>

              <div className="al-sep pt-5 text-center text-sm text-gray-500 dark:text-gray-400">
                Non hai ricevuto il codice?{' '}
                <button type="button" onClick={handleResendOTP} className={linkClass}>Invia di nuovo</button>
              </div>
            </form>
          )}
          </div>

          {/* Footer */}
          <div className="mt-6 space-y-2 text-center lg:text-left">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Problemi di accesso?{' '}
              <button type="button" onClick={() => navigate('/contact')} className="font-semibold text-primary-700 underline underline-offset-4 hover:text-primary-800 dark:text-primary-400">
                Contattaci
              </button>
            </p>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="text-xs text-gray-400 hover:text-gray-600 hover:underline dark:text-gray-500 dark:hover:text-gray-300"
            >
              Accedi come amministratore di sistema
            </button>
          </div>
        </div>
      </main>

      {/* Modale selezione sede (multi-sede) */}
      <LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onSelect={handleLocationSelect}
        locations={locations}
        loading={loading}
      />
    </div>
  );
};

export default AssociateLoginPage;

